"use client"
import { useState, useEffect, useRef } from "react"
import useAutoIndex from "../../hooks/useAutoIndex"
import Hero1images from "../../assets/images/hero-1-2.jpeg";
import Hero2images from "../../assets/images/hero2.png";
import Hero3images from "../../assets/images/hero3.png";
import styles from "./Hero.module.css"

export default function Hero() {
  const slides = [
    { src: Hero1images, title: "Expert Hands,", subtitle: "Natural Results" },
    { src: Hero2images, title: "Perfect Balance,", subtitle: "Beautiful Breathing" },
    { src: Hero3images, title: "Maximum Density,", subtitle: "Natural Look" },
  ]
  
  // Sonsuz döngü için genişletilmiş slaytlar dizisini oluştur
  const extendedSlides = [
    slides[slides.length - 1], // Last slide at the beginning
    ...slides, // Original slides
    slides[0] // First slide at the end
  ]
  
  const [index, setIndex] = useAutoIndex(slides.length, 6000)
  const [isDragging, setIsDragging] = useState(false)
  const [dragX, setDragX] = useState(0)
  const [startX, setStartX] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const containerRef = useRef(null)
  
  // Görüntüleme indeksini genişletilmiş indekse dönüştür
  const displayIndex = index + 1
  
  // Dairesel gezinmeden sonra sorunsuz geçişleri yönet
  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    
    // Dairesel geçişten sonra pozisyonu sıfırla
    const resetPosition = () => {
      container.style.transition = 'none'
      if (index === 0) {
        // Gerçek ilk slayta atla (genişletilmiş dizide indeks 1)
        container.style.transform = 'translateX(-100%)'
      } else if (index === slides.length - 1) {
        // Gerçek son slayta atla (genişletilmiş dizide indeks slides.length)
        container.style.transform = `translateX(-${slides.length * 100}%)`
      }
      setTimeout(() => {
        container.style.transition = 'transform 0.6s ease'
      }, 50)
    }
    
    // Sadece sınırdayken ve sürüklenmiyorken sıfırla
    if (!isDragging && (index === 0 || index === slides.length - 1)) {
      const timeoutId = setTimeout(resetPosition, 600) // Geçiş tamamlandıktan sonra
      return () => clearTimeout(timeoutId)
    }
  }, [index, isDragging, slides.length])
  

  // Sürükleme işleyicileri
  const handleStart = (e) => {
    setIsDragging(true)
    setStartX(e.clientX || (e.touches && e.touches[0].clientX))
    setDragX(0)
    setIsTransitioning(false)
  }

  const handleMove = (e) => {
    if (!isDragging) return
    const currentX = e.clientX || (e.touches && e.touches[0].clientX)
    const diff = currentX - startX
    setDragX(diff)
  }

  const handleEnd = () => {
    if (!isDragging) return
    
    const threshold = 50
    setIsDragging(false)
    
    if (dragX > threshold) {
      // Sağa kaydır - önceki slayta git
      if (index === 0) {
        // Özel durum: son slayta git
        const container = containerRef.current
        if (container) {
          container.style.transition = 'transform 0.6s ease'
          setIndex(slides.length - 1)
        }
      } else {
        setIndex(index - 1)
      }
    } else if (dragX < -threshold) {
      // Sola kaydır - sonraki slayta git
      if (index === slides.length - 1) {
        // Özel durum: ilk slayta git
        const container = containerRef.current
        if (container) {
          container.style.transition = 'transform 0.6s ease'
          setIndex(0)
        }
      } else {
        setIndex(index + 1)
      }
    }
    
    setDragX(0)
  }

  // Olay dinleyicileri
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMove)
      document.addEventListener('mouseup', handleEnd)
      document.addEventListener('touchmove', handleMove, { passive: false })
      document.addEventListener('touchend', handleEnd)
    }

    return () => {
      document.removeEventListener('mousemove', handleMove)
      document.removeEventListener('mouseup', handleEnd)
      document.removeEventListener('touchmove', handleMove)
      document.removeEventListener('touchend', handleEnd)
    }
  }, [isDragging, dragX, startX, index, slides.length])

  // Sonsuz döngü desteğiyle dönüşümü hesapla
  const getTransform = () => {
    let baseTransform = -displayIndex * 100
    const dragOffset = isDragging ? dragX / (containerRef.current?.offsetWidth || 1) * 100 : 0
    
    // Sürükleme sırasında dairesel geçişleri yönet
    if (isDragging) {
      if (index === 0 && dragX > 0) {
        // İlk slayttan sağa sürükleniyor - soldan gelen son slaytı göster
        // -100%'den (ilk slayt) başla ve sağa sürükleyerek son slaytı 0%'da göster
        baseTransform = -100 + dragOffset
      } else if (index === slides.length - 1 && dragX < 0) {
        // Son slayttan sola sürükleniyor - sağdan gelen ilk slaytı göster
        // -slides.length*100%'den (son slayt) başla ve sola sürükleyerek ilk slaytı göster
        baseTransform = -slides.length * 100 + dragOffset
      } else {
        baseTransform = -displayIndex * 100 + dragOffset
      }
    } else {
      baseTransform = -displayIndex * 100 + dragOffset
    }
    
    return baseTransform
  }

  return (
    <section className={styles.hero}>
      <div 
        ref={containerRef}
        className={styles.heroSlides} 
        style={{ 
          transform: `translateX(${getTransform()}%)`,
          transition: isDragging ? 'none' : 'transform 0.6s ease'
        }}
        onMouseDown={handleStart}
        onTouchStart={handleStart}
      >
        {extendedSlides.map((s, i) => (
          <div className={styles.heroSlide} key={i}>
            <img src={s.src || "/placeholder.svg"} alt={s.title} />
            <div className={styles.heroCaption}>
              <h1 className={styles.heroTitle}>{s.title}</h1>
              <h2 className={styles.heroSubtitle}>{s.subtitle}</h2>
            </div>
          </div>
        ))}
      </div>
      <div className={styles.heroDots}>
        {slides.map((_, i) => (
          <button
            key={i}
            className={`${styles.dot} ${i === index ? styles.dotActive : ""}`}
            aria-label={`Go to slide ${i + 1}`}
            onClick={() => setIndex(i)}
          />
        ))}
      </div>
    </section>
  )
}
